//==========================================
//⚠️ Keep index files under 100 lines!
//Split logic into submodules as needed.
//==========================================
import { BreadcrumbItem, Breadcrumbs } from "@vds/breadcrumbs";
import { Button } from "@vds/buttons";
import { Loader } from "@vds/loaders";
import { Body, Title } from "@vds/typography";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSearchParams, Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { listRoi } from "../../api/RoiServiceManager";
import {
  CVAPP_CAMERAADJUSTMENT,
  CVAPP_OBJECTBLUR,
} from "../../global/constants/cvappConstants";
import { hasPermission } from "../../global/utility/permissionUtils";
import { getTagName } from "../../model/reportService";
import {
  clearAdvancedConfigAppearances,
  getAllRoiListWithCvApps,
  resetRoi,
  setRoiListWithCvApps,
  setStatistic,
  showNotificationBanner,
  successDeleteRoi,
  updateRoiList,
  patchWidgetConfig
} from "../../store/actions";
import { isRoiWithDeployedStatus } from "../../global/utility/statisticsUtil";
import { BANNER_TYPES } from "../../global/constants/constants";
import {
  LICENSE_NOTIFICATION_MESSAGES,
  LICENSE_NOTIFICATION_TYPES,
  ORIGIN_PAGES,
  ROI_DELETE_STATUS,
  ROI_NOTIFICATION_TYPES,
  UNSUBSCRIBE_FLOW_TEXTS,
  getActiveLicenseSku
} from "../../global/constants/licensesConstants";
import "./roi.css";
import RoiList from "./RoiList/RoiList";

const RegionOfInterest = () => {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const cameraList = useSelector((reduxState) => reduxState.deviceReducer.cameraList);
  const fovList = useSelector((reduxState) => reduxState.deviceReducer.fovList);
  const urlCameraId = searchParams.get("cameraId");
  const urlFovId = searchParams.get("fovId");
  const cameraData = state?.data ?? cameraList.find((c) => c.id === urlCameraId);
  const streamData = state?.streamData ?? fovList.filter((fov) => fov.parentdeviceid === urlCameraId);
  const FOVId = state?.FOVId ?? urlFovId;
  const FOVSnapshot = state?.FOVSnapshot ?? null;
  const fieldOfViews = state?.fieldOfViews ?? streamData;
  const FOVName = streamData ? streamData[0]?.name : "";
  const isLicenseSuspended = state?.licenseStatus?.toLowerCase() === 'suspended';
  const isUnsubscribeFlow = state?.isUnsubscribeFlow;
  const isCvAppSubOrigin = state?.originPage === ORIGIN_PAGES.CV_APP_SUB;
  const isLicenseDetailsOrigin = state?.originPage === ORIGIN_PAGES.LICENSE_DETAILS;  
  const editAppsLabel = isUnsubscribeFlow
    ? UNSUBSCRIBE_FLOW_TEXTS.UNSUBSCRIBE_APPS_BREADCRUMB
    : UNSUBSCRIBE_FLOW_TEXTS.EDIT_APPS_BREADCRUMB;
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const widgetConfig = useSelector((state) => state.widgetReducer.widgetConfig);
  const reducerRoiList = useSelector((state) => state.deviceReducer.roiList);
  const roiListItem = useSelector((state) => state.polygonRoiReducer?.roiListWithCvapps?.filter(isRoiWithDeployedStatus));
  const roiListWithCvappsRaw = useSelector((state) => state.polygonRoiReducer?.roiListWithCvapps);
  const navigate = useNavigate();
  const selectedFovIdTabIndex = streamData?.findIndex(
    (item) => item.id === FOVId
  );
  const roiDeleteResponse = useSelector(
    (state) => state.polygonRoiReducer.deleteRes
  );
  const [pendingCvAppSubNav, setPendingCvAppSubNav] = useState(false);
  const [activeFOVTab, setActiveFOVTab] = useState({
    fovName: FOVName,
    fovId: FOVId,
    snapShot: FOVSnapshot,
  });
  const defaultPoints = {
    polygon: {
      edges: [
        {
          pointA: {
            x: 2,
            y: 4,
          },
          pointB: {
            x: 1912,
            y: 2,
          },
        },
        {
          pointA: {
            x: 1912,
            y: 2,
          },
          pointB: {
            x: 1915,
            y: 1071,
          },
        },
        {
          pointA: {
            x: 1915,
            y: 1071,
          },
          pointB: {
            x: 0,
            y: 1073,
          },
        },
        {
          pointA: {
            x: 0,
            y: 1073,
          },
          pointB: {
            x: 2,
            y: 4,
          },
        },
      ],
      frame: {
        maxX: 1919,
        maxY: 1079,
        minX: 0,
        minY: 0,
      },
    },
  };
  const initialRoiListData = roiListItem?.filter((r) => r.parentdeviceid === FOVId) ?? [];
  const [roiListData, setRoiListData] = useState(initialRoiListData);
  const [loading, setLoading] = useState(false);
  const [floorImage, setFloorImage] = useState("");
  const [roiData, setRoiData] = useState([]);
  const [selectedRoi, setSelectedRoi] = useState([]);
  const [roiIds, setRoiIds] = useState([]);
  const [showModal, setShowModal] = useState({
    isOpen: false,
  });
  const [showCvAppsModal, setShowCvAppsModal] = useState(false);
  const [deleteBannerNotification, setDeleteBannerNotification] =
    useState(false);
  const [bulkUndeploySuccess, setBulkUndeploySuccess] = useState(false);

  const [filteredAppList, setFilteredAppList] = useState([]);
  const tagIdList = useSelector((state) => {
    return state.sitesReducer.tagIdList;
  });

  useEffect(() => {
    setLoading(true);
    fetchRoiList(activeFOVTab.fovId);
  }, [activeFOVTab?.fovId]);

  useEffect(() => {
    setRoiIds([]);
    setSelectedRoi([]);
    
    if (typeof roiDeleteResponse === "string") {
      setRoiData([]);
      //Regular bulk undeploy is refreshed by RoiList's refreshRoi(true), so skip to avoid a double fetch.
      if (
        roiDeleteResponse === ROI_DELETE_STATUS.SUCCESS &&
        (!bulkUndeploySuccess || state?.originPage === ORIGIN_PAGES.CV_APP_SUB)
      ) {
        fetchRoiList(activeFOVTab.fovId);
      }
      try {
        if (roiData?.length > 0) {
          let updatedWidgets = widgetConfig;
          let roiSelected = {};
          roiData.forEach((m) => {
            roiSelected = roiListData.find((f) => f.id === m.roiId);
            updatedWidgets = updatedWidgets.filter((widget) => {
              return !(widget.content.appName === roiSelected?.deployments[0]?.appid && roiSelected?.tagids?.includes(widget.content.location));
            });
          });

          dispatch(patchWidgetConfig({ customdata: { widgetconfig: updatedWidgets } }));
        }
      } catch (error) {
        console.error("Failed to update widget configuration:", error);
      }
    }
    if (roiDeleteResponse === ROI_DELETE_STATUS.SUCCESS) {
      setRoiData([]);
      if (deleteBannerNotification) {
        const notificationInfo = {
          type: ROI_NOTIFICATION_TYPES.ROI_DELETE,
          priority: "success",
          data: {
            title: state?.originPage === ORIGIN_PAGES.CV_APP_SUB
              ? UNSUBSCRIBE_FLOW_TEXTS.UNSUBSCRIBE_SUCCESS
              : UNSUBSCRIBE_FLOW_TEXTS.ROI_DELETE_SUCCESS,
            subTitle: "",
          },
          showNotification: true,
        };
        dispatch(showNotificationBanner(notificationInfo));
        setDeleteBannerNotification(false);
      }else{
        fetchRoiList(activeFOVTab.fovId);
      }
      dispatch(successDeleteRoi([])); // Reset to allow subsequent deletes
      if (bulkUndeploySuccess) {
        setBulkUndeploySuccess(false);
      }
      if (state?.originPage === ORIGIN_PAGES.CV_APP_SUB) {
        setPendingCvAppSubNav(true);
        return;
      }
    } else if (roiDeleteResponse === ROI_DELETE_STATUS.ERROR) {
      setLoading(false);
      if (bulkUndeploySuccess) {
        setBulkUndeploySuccess(false);
      }
      if (deleteBannerNotification) {
        const notificationInfo = {
          type: ROI_NOTIFICATION_TYPES.ROI_DELETE,
          priority: "critical",
          data: {
            title: ROI_NOTIFICATION_TYPES.ROI_DELETE,
            subTitle: state?.originPage === ORIGIN_PAGES.CV_APP_SUB
              ? UNSUBSCRIBE_FLOW_TEXTS.UNSUBSCRIBE_FAILURE
              : UNSUBSCRIBE_FLOW_TEXTS.ROI_DELETE_FAILURE,
          },
          showNotification: true,
        };
        dispatch(showNotificationBanner(notificationInfo));
        setDeleteBannerNotification(false);
      }
      dispatch(successDeleteRoi([]));
    }
    

  }, [roiDeleteResponse]);

  useEffect(() => {
    if (cameraData && cameraData.length === 0) {
      navigate("/devices");
    }
  }, []);

  useEffect(() => {
    if (pendingCvAppSubNav && !loading) {
      setPendingCvAppSubNav(false);
      // Pass the fresh post-delete roiList so cvAppSub reflects the updated count
      // (especially when the last app is removed and the FOV has zero apps left)
      const freshRoiList =
        roiListWithCvappsRaw?.filter((f) => f?.parentdeviceid === FOVId) ?? [];
      navigate("/cvAppSub", {
        state: { ...state?.cvAppSubReturnState, roiList: freshRoiList },
      });
    }
  }, [pendingCvAppSubNav, loading]);

  const handleDeleteClick = () => {
    setBulkUndeploySuccess(false); // Reset bulk flag for individual delete
    setDeleteBannerNotification(true);
    if (!hasPermission("Apps", "Delete")) {
      showPermissionsBanner();
    } else {
      setShowModal({
        isOpen: true,
      });
    }
  };

  const fetchRoiList = (fovId) => {
    const payload = {
      accountname: activeSession?.accountId,
      fovidentifiers: [
        {
          deviceid: fovId,
        },
      ],
      filter: {
        $expand: "fields,cvapp,deploy",
      },
    };

    const roiListApiResponse = async (payload, activeSession) => {
      try {
        const roiListData = await listRoi(payload, activeSession);
        //Filter the default roi that is created by the system without polygon data
        const data = roiListData.filter(
          (item) =>
            (item.customdata && item.customdata.polygon) ||
            item?.cvapps != undefined
        );
        setRoiListData(data);
        const mergedRoiList = [
          ...(reducerRoiList || []).filter((roi) => roi.parentdeviceid !== fovId),
          ...data,
        ];
        dispatch(updateRoiList(mergedRoiList));
        dispatch(setRoiListWithCvApps(mergedRoiList));

        setLoading(false);
      } catch (e) {
        console.log("Error fetching data");
        setLoading(false);
      }
    };
    roiListApiResponse(payload, activeSession);
  };

  const createRoiList = () => {
    //    setLoading(true);
    let filterAppList = new Map();
    roiListData &&
      roiListData.length &&
      roiListData?.forEach((item, index) => {
        const { cvapps } = item;
        //Removing duplicates from cvapps
        const cvappsSet = Array.from(new Set(cvapps?.map((a) => a.id))).map(
          (id) => {
            return cvapps?.find((a) => a.id === id);
          }
        );
        if (cvappsSet?.length > 0) {
          cvappsSet.forEach((app) => {
            const appName = app?.resources.displayNameFull ?? app?.name;
            if (!filterAppList.has(appName)) {
              filterAppList.set(appName, [
                {
                  id: item?.id,
                  polygon:
                    appName === CVAPP_OBJECTBLUR ||
                    appName === CVAPP_CAMERAADJUSTMENT
                      ? defaultPoints
                      : item?.customdata?.polygon,
                  cvappid: app?.id,
                  category: item?.cvapps[0]?.category,
                  roiName: item?.name,
                  deployments: item?.deployments,
                  FOVId: activeFOVTab.fovId,
                  cameraId: cameraData.id,
                  companionId: cameraData.parentdeviceid,
                  tagIds: item?.tagids,
                },
              ]);
            } else {
              filterAppList.get(appName).push({
                id: item?.id,
                polygon:
                  appName === CVAPP_OBJECTBLUR ||
                  appName === CVAPP_CAMERAADJUSTMENT
                    ? defaultPoints
                    : item?.customdata?.polygon,
                cvappid: app?.id,
                category: item?.cvapps[0]?.category,
                roiName: item?.name,
                deployments: item?.deployments,
                FOVId: activeFOVTab.fovId,
                cameraId: cameraData.id,
                companionId: cameraData.parentdeviceid,
                tagIds: item?.tagids,
              });
            }
          });
        }
      });
    setFilteredAppList(filterAppList);
  };

  const getTags = (tagids) => {
    const statistics = [];
    if (tagids?.length > 0)
      tagids?.map((id) => {
        statistics.push(getTagName(id, tagIdList));
      });
    return statistics;
  };

  const showLicenseSuspendedBanner = () => {
    const notificationInfo = {
      type: BANNER_TYPES.INFORMATION,
      priority: BANNER_TYPES.WARNING,
      showNotification: true,
      title: LICENSE_NOTIFICATION_TYPES.LICENSE_SUSPENDED,
      subtitle: LICENSE_NOTIFICATION_MESSAGES.LICENSE_SUSPENDED_MESSAGE,
    };
    dispatch(showNotificationBanner(notificationInfo));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditRoi = () => {
    if (isLicenseSuspended) {
      showLicenseSuspendedBanner();
      return;
    }
    if (!hasPermission("Apps", "Edit")) {
      showPermissionsBanner();
    } else {
      dispatch(resetRoi());
      if (
        selectedRoi &&
        selectedRoi.length &&
        selectedRoi[0]?.deployments &&
        selectedRoi[0]?.deployments[0]?.config["deploy-parameters"]
      ) {
        const deploymentData =
          selectedRoi[0]?.deployments[0]?.config["deploy-parameters"];
        if (deploymentData?.zone1 || deploymentData?.zone2) {
          const zones = [
            {
              groupName: selectedRoi[0]?.groupName,
              statisticData:
                deploymentData["zone1"]?.length > 0
                  ? getTags(deploymentData["zone1"])
                  : ["Unused"],
              tags:
                deploymentData["zone1"]?.length > 0
                  ? deploymentData["zone1"]
                  : ["-1"],
              eeAppStatus: "Zone1",
            },
            {
              groupName: selectedRoi[0]?.groupName,
              statisticData:
                deploymentData["zone2"].length > 0
                  ? getTags(deploymentData["zone2"])
                  : ["Unused"],
              tags:
                deploymentData["zone2"].length > 0
                  ? deploymentData["zone2"]
                  : ["-1"],
              eeAppStatus: "Zone2",
            },
          ];
          dispatch(setStatistic(zones));
        }
      }

      const selectedState = {
        cvapps: selectedRoi,
        roiUpdateData: roiData,
        FOVId: activeFOVTab.fovId,
        cameraId: cameraData.id,
        companionId: cameraData.parentdeviceid,
        action: "update",
        cameraData: cameraData,
        streamData: streamData,
        FOVSnapshot: activeFOVTab.snapShot,
        fieldOfViews: fieldOfViews,
        FOVName: activeFOVTab.fovName,
        location: "roilist",
        cameraname: cameraData?.name,
        cvappid: selectedRoi[0].cvappid,
        tagids: selectedRoi[0]?.tagids,
        deployParameters:
          selectedRoi && selectedRoi.length && selectedRoi[0]?.deployments
            ? selectedRoi[0]?.deployments[0]?.config["deploy-parameters"]
            : [],
        deploymentId:
          selectedRoi && selectedRoi.length && selectedRoi[0]?.deployments
            ? selectedRoi[0]?.deployments[0]?.id
            : "",
        // getActiveLicenseSku picks the primary SKU; add-ons (e.g. EA) are excluded.
        sku: getActiveLicenseSku(streamData[0]?.licenses),
        roiList: roiData,
        siteSelected: cameraData.siteids,
        originPage: state?.originPage ? state?.originPage : "",
        record: {},
      };
      navigate("/polygon", { state: selectedState });
    }
  };

  useEffect(() => {
    createRoiList();
  }, [roiListData]);

  const showPermissionsBanner = () => {
    const notificationInfo = {
      type: "No Permission",
      priority: "critical",
      showNotification: true,
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  const showNoRoisBanner = () => {
    const notificationInfo = {
      type: "No ROIs",
      priority: "info",
      data: {
        title: "License removed from stream",
        subTitle:
          "You have unsubscribed to all apps on this stream. The license for this stream has been removed and is now available to be attached to other streams.",
      },
      showNotification: true,
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  const handleAddAppsInRoi = () => {
    dispatch(clearAdvancedConfigAppearances());
    if (isLicenseSuspended) {
      showLicenseSuspendedBanner();
      return;
    }
    if (!hasPermission("Apps", "Create")) {
      showPermissionsBanner();
    } else {
      const state = {
        fieldOfViews: fieldOfViews,
        FOVImagesName: floorImage,
        FOVImageURL: floorImage,
        FOVNames: activeFOVTab.fovName,
        FOVId: activeFOVTab.fovId,
        cameraId: cameraData.id,
        companionId: cameraData.parentdeviceid,
        cameraname: cameraData?.name,
        cameradata: cameraData,
        data: cameraData,
        roiList: roiData,
        // getActiveLicenseSku picks the primary SKU; add-ons (e.g. EA) are excluded.
        sku: getActiveLicenseSku(streamData[0]?.licenses),
        siteSelected: cameraData?.customdata?.location?.site,
        originPage: "",
        floorSelected: cameraData?.customdata?.location?.floor,
        areaSelected: cameraData?.customdata?.location?.area,
        subAreaSelected: cameraData?.customdata?.location?.subarea,
      };
      sessionStorage.setItem("selectedCameraInfo", JSON.stringify(state));
      if (streamData[0]?.licenses && streamData[0]?.licenses.length > 0) {
        navigate("/cvAppSub", { state: state });
      } else {
        navigate("/cvAppsLicenseType", { state: state });
      }
    }
  };

  return (
    <div>
      {loading ? (
        <Loader fullscreen={false} active={true} surface="light" />
      ) : (
        <>
          <div className="roi-header-col">
            <div className="roi-edit-apps-header">
                <Breadcrumbs surface="light">
                  {[
                    isUnsubscribeFlow && isCvAppSubOrigin ? (
                      <BreadcrumbItem key="root-unsubscribe">
                        <Link
                          to={{
                            pathname: "/cameraInfoPage",
                            search: `?${createSearchParams({ cameraId: cameraData?.id })}`,
                          }}
                          state={{ data: cameraData }}
                          className="breadcrums-item_link"
                        >
                          {state?.cameraname || cameraData?.name || UNSUBSCRIBE_FLOW_TEXTS.CAMERA_FALLBACK}
                        </Link>
                      </BreadcrumbItem>
                    ) : isLicenseDetailsOrigin ? (
                      <BreadcrumbItem key="root-settings">
                        <Link
                          data-testid="polygon_devicesLink"
                          to="/settings"
                          state={{ tabName: "Settings" }}
                          className="breadcrums-item_link"
                        >
                          Settings
                        </Link>
                      </BreadcrumbItem>
                    ) : (
                      <BreadcrumbItem key="root-cameras">
                        <Link
                          data-testid="polygon_devicesLink"
                          to="/devices"
                          state={{ tabName: "Camera" }}
                          className="breadcrums-item_link"
                        >
                          {UNSUBSCRIBE_FLOW_TEXTS.CAMERAS_BREADCRUMB || "Cameras"}
                        </Link>
                      </BreadcrumbItem>
                    ),
                    (state?.originPage === ORIGIN_PAGES.CAMERA_DETAILS || state?.originPage === "cameraDetails") && (
                      <BreadcrumbItem key="origin-camera-details">
                        <Link
                          data-testid="polygon_cameraInfoPageLink"
                          to={{
                            pathname: "/cameraInfoPage",
                            search: `?${createSearchParams({ cameraId: cameraData?.id })}`,
                          }}
                          state={{ data: cameraData }}
                          className="breadcrums-item_link"
                        >
                          {cameraData?.name}
                        </Link>
                      </BreadcrumbItem>
                    ),
                    isLicenseDetailsOrigin && (
                      <BreadcrumbItem key="origin-license-details">
                        <Link
                          to={{ pathname: "/licensedetails" }}
                          className="breadcrums-item_link"
                        >
                          License Details
                        </Link>
                      </BreadcrumbItem>
                    ),
                    isUnsubscribeFlow && isCvAppSubOrigin && (
                      <BreadcrumbItem key="cvapp-config">
                        <Link
                          to="/cvAppSub"
                          state={state?.cvAppSubReturnState}
                          className="breadcrums-item_link"
                        >
                          {UNSUBSCRIBE_FLOW_TEXTS.CV_APP_CONFIG_BREADCRUMB}
                        </Link>
                      </BreadcrumbItem>
                    ),
                    <BreadcrumbItem key="edit-apps">
                      {editAppsLabel || "Edit Apps"}
                    </BreadcrumbItem>,
                  ].filter(Boolean)}
                </Breadcrumbs>
              <div className="roi-edit-apps-title">
                <Title
                  size="large"
                  color="#000000"
                  primitive="h4"
                  viewport="desktop"
                >
                {editAppsLabel}
                </Title>
              </div>

              <div className="roi-edit-apps-subtitle">
                <Body size="large" color="#000000" primitive="h6">
              {cameraData?.name && FOVName ? `${cameraData.name}/${FOVName}` : ""}
                </Body>
              </div>
            </div>
          </div>

          {filteredAppList.size > 0 ? (
            <div>
              <RoiList
                filteredAppList={filteredAppList}
                showModal={showModal}
                activeFOVTab={activeFOVTab}
                cameraData={cameraData}
                streamData={streamData}
                setShowModal={setShowModal}
                snapshot={floorImage}
                setFloorImage={setFloorImage}
                FOVName={FOVName}
                setRoiData={setRoiData}
                roiData={roiData}
                selectedRoi={selectedRoi}
                setSelectedRoi={setSelectedRoi}
                roiIds={roiIds}
                setRoiIds={setRoiIds}
                FOVSnapshot={activeFOVTab.snapShot}
                fieldOfViews={fieldOfViews}
                state={state}
                onEdit={handleEditRoi}
                onDelete={handleDeleteClick}
                showLoader={setLoading}
                showCvAppsModal={showCvAppsModal}
                refreshRoi={(status) =>
                  status ? fetchRoiList(activeFOVTab.fovId) : ""
                }
                isLicenseSuspended={isLicenseSuspended}
                setDeleteBannerNotification={setDeleteBannerNotification}
                setBulkUndeploySuccess={setBulkUndeploySuccess}
                isUnsubscribeFlow={isUnsubscribeFlow}
              />
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "30px 0 0 0",
                }}
              >
                <Title>This stream is not subscribed to any apps.</Title>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px 0 0 0",
                }}
              >
                <Button
                  use="primary"
                  data-testid="Roi_addAppsButton"
                  onClick={() => handleAddAppsInRoi()}
                >
                  Add Apps
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default RegionOfInterest;